import { useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { CreditSlider } from './CreditSlider';

/**
 * Buying more AI credits, without leaving what you were doing.
 *
 * The rail itself is CreditSlider, shared with the billing page. This file is
 * only the box around it: opened from the menubar badge when credits run low,
 * and from inside the chat at the moment the agent stops answering, which is
 * where somebody is most willing to fix it and least willing to go hunting.
 *
 * It asks /billing/plans rather than the workspace's billing summary, because
 * neither of those two surfaces has any reason to load a full summary to draw
 * eight rungs and a price.
 */

type Pack = {
  code: string;
  credits: number;
  price_usd: number;
  price_bhd: number;
  percent_off: number;
  popular?: boolean;
};

export function TopUpDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t, lang } = useI18n();
  const { entitlements } = useAuth();

  const plans = useQuery({
    queryKey: ['billing-plans-packs'],
    queryFn: () => api<{ packs: Pack[]; plans: { code: string; included_ai_credits: number }[] }>('/billing/plans'),
    enabled: open,
  });

  const packs = plans.data?.packs ?? [];

  /* What the plan already gives, so the total below is the truth and not a
     guess. Falls back to zero rather than to a plausible number. */
  const included = useMemo(() => {
    const code = entitlements?.plan_code;
    return plans.data?.plans?.find((p) => p.code === code)?.included_ai_credits ?? 0;
  }, [plans.data, entitlements?.plan_code]);

  const checkout = useMutation({
    mutationFn: (code: string) =>
      api<{ url: string }>('/billing/credits/checkout', {
        method: 'POST',
        body: JSON.stringify({ pack: code }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: (r) => {
      window.location.href = r.url;
    },
  });

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="topup-title"
      dir={lang === 'ar' ? 'rtl' : 'ltr'}
      className="fixed inset-0 z-[90] flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-lg rounded-2xl border border-border bg-card p-7 shadow-[var(--elev-3)]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="topup-title" className="text-[19px] font-semibold text-foreground">
              {t('topup_title')}
            </h2>
            <p className="mt-1.5 text-[13.5px] text-muted-foreground">{t('topup_subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('close')}
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>

        {packs.length === 0 ? (
          <div className="py-10 text-center text-[13.5px] text-muted-foreground">{t('loading')}</div>
        ) : (
          <>
            <div className="mt-6">
              {/* The same rail the billing page draws. One component, so a
                  change to the ladder cannot land on one surface and not the
                  other, which is exactly how the page ended up showing eight
                  cards while the dialog showed a slider. */}
              <CreditSlider
                packs={packs}
                includedCredits={included}
                locale={lang === 'ar' ? 'ar-EG' : 'en-US'}
                busy={checkout.isPending}
                onBuy={(p) => checkout.mutate(p.code)}
                labels={{
                  credits: t('topup_credits_word'),
                  addition: t('topup_addition'),
                  saving: t('topup_saving'),
                  cta: t('topup_cta'),
                  busy: t('topup_leaving'),
                }}
              />
            </div>
            <p className="mt-3 text-center text-[12px] text-muted-foreground">{t('topup_note')}</p>
          </>
        )}
      </div>
    </div>
  );
}
